// Core
import React, { useMemo } from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Types
import { EquipmentUnitDataVSICrusher } from '../../../../../../types/crusher';
import { MeasurementType } from '../../../../../../types/global';

// Plugins
import { lastOperationalUnitDataVSI } from '../../../../../../plugins/crusher';

// Components
import {
  LineChart,
  LineChartType,
  Series,
} from '../../../../../../layout/elements/LineChart';

// View
import { LubeUnitProps } from './LubeUnit.types';
import { ChartMode } from '../Trendings.types';

function LubeUnit({
  className,
  chartMode,
  operationalData,
  testId,
  trendingLubeUnitData,
}: LubeUnitProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  // Last operational unit snapshot for lube series configuration
  const lastUnitData: EquipmentUnitDataVSICrusher | null = lastOperationalUnitDataVSI(
    operationalData,
  );

  // Generate dynamic ID based on supply flag and chart mode to force chart remount
  const chartId = useMemo((): string => {
    const supplyEnabled: string = String(lastUnitData?.lubeSupplyTempEnable || false);
    const baseId: string = `lube-unit-${chartMode}`;
    return `${baseId}-${supplyEnabled}`;
  }, [chartMode, lastUnitData]);

  const series: Series[] = useMemo(() => [
    {
      name: t('common.cloudConnection'),
      field: 'edgeStatus',
      type: LineChartType.fill,
      date: 'timestamp',
      opposite: true,
      measurementType: MeasurementType.temperature,
    },
    ...(lastUnitData?.lubeSupplyTempEnable ? [{
      name: t('view.crusher.trendings.lubeUnit.legend.supplyTemp'),
      field: 'lubricationSupplyTemperature',
      type: LineChartType.line,
      date: 'timestamp',
      unit: '°C',
      decimals: 2,
      measurementType: MeasurementType.temperature,
      bullet: true,
      minScale: 0,
      maxScale: 60,
    }] : []),
    {
      name: t('view.crusher.trendings.lubeUnit.legend.returnTemp'),
      field: 'lubricationReturnTemperature',
      type: LineChartType.line,
      date: 'timestamp',
      unit: '°C',
      decimals: 2,
      bullet: true,
      measurementType: MeasurementType.temperature,
      minScale: 0,
      maxScale: 60,
    },
    {
      name: t('view.crusher.trendings.lubeUnit.legend.tankTemp'),
      field: 'lubricationTankTemperature',
      type: LineChartType.line,
      date: 'timestamp',
      unit: '°C',
      decimals: 2,
      bullet: true,
      measurementType: MeasurementType.temperature,
      minScale: 0,
      maxScale: 60,
    },
  ], [t, lastUnitData]);

  return (
    <LineChart
      activeBullet={chartMode === ChartMode.live || chartMode === ChartMode.liveAggregate}
      className={className}
      data={trendingLubeUnitData}
      id={chartId}
      series={series}
      testId={testId}
    />
  );
}

export { LubeUnit };
